import React from 'react';
import { IoMdArrowDropdown } from 'react-icons/io';

import { Popover } from 'antd';
import NumericInputField from 'components/common/NumericInputField';
import {
  PRODUCTION_MATRIX_COLUMN_LABEL_PRICE,
  PRODUCTION_MATRIX_COLUMN_LABEL_SCENARIO,
  PRODUCTION_MATRIX_COLUMN_LABEL_YIELD,
  PRODUCTION_MATRIX_REQUIRED_PLACEHOLDER,
  PRODUCTION_MATRIX_ROW_LABEL_HIGH,
  PRODUCTION_MATRIX_ROW_LABEL_LOW,
  PRODUCTION_MATRIX_ROW_LABEL_MEDIUM,
  PRODUCTION_STEP_GROUP_KEY,
  TASKS_GRID_STEP_ACTIONS_ARIA_LABEL,
} from 'constants/uiConstants';
import {
  IInputStep,
  IInputStepProduction,
  InputStepType,
} from 'interfaces/IInputStep';
import type { IInputTask } from 'interfaces/IInputTask';
import { formatCurrency } from 'utils/CurrencyUtils';
import MergeClassNames from 'utils/MergeClassNames';

import {
  focusAdjacentStepCell,
  STEP_NAV_COL_ATTR,
  STEP_NAV_COL_PRODUCTION_PRICE,
  STEP_NAV_COL_PRODUCTION_YIELD,
} from './focusAdjacentStepCell';
import { getTaskSteps } from './stepCalculations';
import { ProductionMatrixKey, StepSelection } from './stepFactory';
import StepInputSelector from './StepInputSelector';
import { RowKebabButton } from './TaskRowContextMenu';
import type { useStepContextMenu } from './useStepContextMenu';

type StepContextMenuApi = ReturnType<typeof useStepContextMenu>;

interface MatrixRow {
  label: string;
  yieldKey: ProductionMatrixKey;
  priceKey: ProductionMatrixKey;
  isRequired: boolean;
}

const MATRIX_ROWS: MatrixRow[] = [
  {
    label: PRODUCTION_MATRIX_ROW_LABEL_LOW,
    yieldKey: 'yieldLow',
    priceKey: 'priceLow',
    isRequired: false,
  },
  {
    label: PRODUCTION_MATRIX_ROW_LABEL_MEDIUM,
    yieldKey: 'yield',
    priceKey: 'price',
    isRequired: true,
  },
  {
    label: PRODUCTION_MATRIX_ROW_LABEL_HIGH,
    yieldKey: 'yieldHigh',
    priceKey: 'priceHigh',
    isRequired: false,
  },
];

const HEADER_CELL_CLASS =
  'border border-gray-medium px-2 py-1.5 text-center text-sm font-semibold';

interface ProductionTaskStepsProps {
  task: IInputTask;
  editingStepInputId: string | null;
  setEditingStepInputId: (stepId: string | null) => void;
  handleStepInputChange: (
    task: IInputTask,
    stepId: string,
    selections: StepSelection[]
  ) => void;
  commitStepField: (
    task: IInputTask,
    stepId: string,
    patch: Partial<IInputStep>
  ) => void;
  stepContextMenu: StepContextMenuApi;
  renderTaskDetailsSection: (task: IInputTask) => React.ReactNode;
  renderInputTableFooter: (
    task: IInputTask,
    hiddenCount: number
  ) => React.ReactNode;
}

/**
 * A Production task's expanded row: the details card, then either the "+ Add"
 * footer (no item yet) or the Low/Medium/High × Yield/Price matrix of its
 * single production step. Cells commit on blur through the standard autosave.
 */
const ProductionTaskSteps: React.FC<ProductionTaskStepsProps> = ({
  task,
  editingStepInputId,
  setEditingStepInputId,
  handleStepInputChange,
  commitStepField,
  stepContextMenu,
  renderTaskDetailsSection,
  renderInputTableFooter,
}) => {
  const step = getTaskSteps(task).find(
    (candidate): candidate is IInputStepProduction =>
      candidate.stepType === InputStepType.Production
  );

  if (!step) {
    return (
      <div className="input-task-steps-wrapper ml-7 pb-6">
        {renderTaskDetailsSection(task)}
        {renderInputTableFooter(task, 0)}
      </div>
    );
  }

  const { contextMenuState } = stepContextMenu;
  const isHighlighted =
    contextMenuState?.target.rowType === 'step' &&
    contextMenuState.target.taskId === task.taskId &&
    contextMenuState.target.stepId === step.stepId;

  const renderValueCell = (
    key: ProductionMatrixKey,
    isRequired: boolean,
    formatValue: (value: number) => React.ReactNode,
    isLastRow: boolean,
    navColumn: string
  ) => {
    const value = step[key] ?? null;
    const emptyDisplay = isRequired ? (
      <span className="text-gray-muted">
        {PRODUCTION_MATRIX_REQUIRED_PLACEHOLDER}
      </span>
    ) : (
      ' '
    );
    return (
      <td
        className={MergeClassNames(
          'relative border-r border-gray-medium p-0',
          !isLastRow && 'border-b'
        )}
        {...{ [STEP_NAV_COL_ATTR]: navColumn }}
      >
        <div className="absolute inset-0">
          <NumericInputField
            className="h-full w-full"
            inputClassName="h-full w-full"
            min={0}
            inclusiveMin
            allowEmpty={!isRequired}
            value={value}
            displayValue={value === null ? emptyDisplay : formatValue(value)}
            onCommit={(next) =>
              commitStepField(task, step.stepId, { [key]: next })
            }
            onArrowNavigate={focusAdjacentStepCell}
          />
        </div>
      </td>
    );
  };

  return (
    <div className="input-task-steps-wrapper ml-7 pb-6">
      {renderTaskDetailsSection(task)}
      <div
        className="input-task-steps-table border-l-2 border-success-dark pb-4 pl-6"
        onClick={(event) => {
          event.stopPropagation();
          stepContextMenu.closeContextMenu();
        }}
        onContextMenu={(event) =>
          stepContextMenu.handleStepRowContextMenu(
            event,
            task.taskId,
            step.stepId
          )
        }
      >
        {/* Sits above the table at one column's width, so the picker opens beside it */}
        <div className="group mb-2 flex w-[40%] items-center gap-1">
          <Popover
            trigger="click"
            placement="right"
            autoAdjustOverflow
            destroyOnHidden
            getPopupContainer={() => document.body}
            open={editingStepInputId === step.stepId}
            onOpenChange={(open) => {
              stepContextMenu.closeContextMenu();
              setEditingStepInputId(open ? step.stepId : null);
            }}
            classNames={{ root: 'step-input-selector-popover' }}
            content={
              <StepInputSelector
                taskType={task.taskType}
                initialCategoryKey={PRODUCTION_STEP_GROUP_KEY}
                initialSearchText={step.item}
                onClose={() => setEditingStepInputId(null)}
                onSelect={(selections) =>
                  handleStepInputChange(task, step.stepId, selections)
                }
                task={task}
              />
            }
          >
            <div
              className={MergeClassNames(
                'flex min-w-0 flex-1 cursor-pointer items-center rounded border border-gray-medium bg-white px-2 py-1.5 font-semibold text-charcoal hover:border-success',
                isHighlighted && 'border-success'
              )}
            >
              <div className="block w-full flex-1 truncate">{step.item}</div>
              <IoMdArrowDropdown className="ml-1 flex-shrink-0 text-gray-dark" />
            </div>
          </Popover>
          <RowKebabButton
            label={TASKS_GRID_STEP_ACTIONS_ARIA_LABEL}
            isActive={isHighlighted}
            onOpen={(event) =>
              stepContextMenu.openMenuFromKebab(event, {
                rowType: 'step',
                taskId: task.taskId,
                stepId: step.stepId,
              })
            }
          />
        </div>
        {/* ! overrides antd's border-collapse: separate, which doubles shared borders */}
        <table className="w-full table-fixed !border-collapse text-base text-gray-dark">
          <thead>
            <tr>
              <th className={MergeClassNames('w-[40%]', HEADER_CELL_CLASS)}>
                {PRODUCTION_MATRIX_COLUMN_LABEL_SCENARIO}
              </th>
              <th className={MergeClassNames('w-[30%]', HEADER_CELL_CLASS)}>
                {PRODUCTION_MATRIX_COLUMN_LABEL_YIELD}
              </th>
              <th className={MergeClassNames('w-[30%]', HEADER_CELL_CLASS)}>
                {PRODUCTION_MATRIX_COLUMN_LABEL_PRICE}
              </th>
            </tr>
          </thead>
          <tbody>
            {MATRIX_ROWS.map((row, index) => {
              const isLastRow = index === MATRIX_ROWS.length - 1;
              return (
                <tr
                  key={row.yieldKey}
                  className={MergeClassNames(
                    'group bg-white',
                    isHighlighted && 'highlighted-row'
                  )}
                >
                  <td
                    className={MergeClassNames(
                      'border-l border-r border-gray-medium px-2 py-1',
                      !isLastRow && 'border-b'
                    )}
                  >
                    {row.label}
                  </td>
                  {renderValueCell(
                    row.yieldKey,
                    row.isRequired,
                    (value) => value,
                    isLastRow,
                    STEP_NAV_COL_PRODUCTION_YIELD
                  )}
                  {renderValueCell(
                    row.priceKey,
                    row.isRequired,
                    (value) => formatCurrency(value),
                    isLastRow,
                    STEP_NAV_COL_PRODUCTION_PRICE
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductionTaskSteps;
